import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalShellComponent } from '../../modals/modal-shell/modal-shell.component';
import { LoaderComponent } from '../../common/loader/loader.component';
import { TenantServiceDbService } from '../../services/tenant-service-db.service';
import { ToastrService } from '../../services/toastr.service';
import {
  DatabricksCredentials,
  DbConfigResponse,
  DbCredentials,
  DbType,
  MssqlCredentials,
  SnowflakeCredentials
} from '../../models/tenant-service-db.model';
import { ApiErrorResponse } from '../../models/auth.model';

type WizardStep = 'select' | 'form';

interface DbOption {
  type: DbType;
  label: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-tenant-service-db',
  imports: [ModalShellComponent, ReactiveFormsModule, LoaderComponent],
  templateUrl: './tenant-service-db.component.html',
  styleUrl: './tenant-service-db.component.scss'
})
export class TenantServiceDbComponent implements OnInit {
  @Input() tenantUid = '';
  @Input() preloadedConfig?: DbConfigResponse | null;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  readonly dbOptions: DbOption[] = [
    { type: 'MSSQL', label: 'MSSQL / Synapse', description: 'Azure Synapse or SQL Server', icon: 'bi-server' },
    { type: 'SNOWFLAKE', label: 'Snowflake', description: 'Snowflake data warehouse', icon: 'bi-snow2' },
    { type: 'DATABRICKS', label: 'Databricks', description: 'Databricks SQL warehouse', icon: 'bi-hexagon-fill' }
  ];

  step: WizardStep = 'select';
  selectedType: DbType | null = null;
  isEditing = false;

  private originalType: DbType | null = null;
  private originalConfig: DbCredentials | null = null;

  loading = true;
  switchingType = false;
  saving = false;

  revealedFields = new Set<string>();

  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private tenantServiceDbService: TenantServiceDbService,
    private toastrService: ToastrService
  ) { }

  ngOnInit(): void {
    if (this.preloadedConfig !== undefined) {
      this.applyExisting(this.preloadedConfig);
      return;
    }
    this.loadExisting();
  }

  get title(): string {
    if (this.step === 'select') {
      return 'Configure Database';
    }
    return this.dbOptions.find(o => o.type === this.selectedType)?.label ?? 'Database Credentials';
  }

  get headerIcon(): string {
    if (this.step === 'select') {
      return 'bi-database-gear';
    }
    return this.dbOptions.find(o => o.type === this.selectedType)?.icon ?? 'bi-database-gear';
  }

  isRevealed(key: string): boolean {
    return this.revealedFields.has(key);
  }

  toggleReveal(key: string): void {
    if (this.revealedFields.has(key)) {
      this.revealedFields.delete(key);
    } else {
      this.revealedFields.add(key);
    }
  }

  selectType(type: DbType): void {
    this.selectedType = type;

    if (type === this.originalType) {
      this.buildForm(type, this.originalConfig);
      this.step = 'form';
      return;
    }

    this.switchingType = true;

    this.tenantServiceDbService.getDbConfig(this.tenantUid, type).subscribe({
      next: (res) => {
        this.switchingType = false;
        this.buildForm(type, res.config);
        this.step = 'form';
      },
      error: () => {
        this.switchingType = false;
        this.buildForm(type, null);
        this.step = 'form';
      }
    });
  }

  changeType(): void {
    this.step = 'select';
  }

  submit(): void {
    if (!this.selectedType || !this.form) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload = { db_type: this.selectedType, ...this.form.getRawValue() };

    this.tenantServiceDbService.saveDbConfig(this.tenantUid, payload).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit();
        this.closed.emit();
      },
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  close(): void {
    if (this.saving) {
      return;
    }
    this.closed.emit();
  }

  private loadExisting(): void {
    this.loading = true;

    this.tenantServiceDbService.getDbConfig(this.tenantUid).subscribe({
      next: (res) => {
        this.loading = false;
        this.applyExisting(res);
      },
      error: () => {
        this.loading = false;
        this.applyExisting(null);
      }
    });
  }

  private applyExisting(res: DbConfigResponse | null): void {
    this.loading = false;

    if (res?.config) {
      this.isEditing = true;
      this.originalType = res.db_type;
      this.originalConfig = res.config;
      this.selectedType = res.db_type;
      this.buildForm(res.db_type, res.config);
      this.step = 'form';
    } else {
      this.isEditing = false;
      this.originalType = null;
      this.originalConfig = null;
      this.step = 'select';
    }
  }

  private buildForm(type: DbType, existing: DbCredentials | null): void {
    if (type === 'MSSQL') {
      const c = existing as MssqlCredentials | null;
      this.form = this.fb.group({
        synapse_server: [c?.synapse_server ?? '', Validators.required],
        synapse_db: [c?.synapse_db ?? '', Validators.required],
        synapse_user: [c?.synapse_user ?? '', Validators.required],
        synapse_password: [c?.synapse_password ?? '', Validators.required],
        synapse_driver: [c?.synapse_driver ?? '{ODBC Driver 17 for SQL Server}', Validators.required],
        synapse_write_user: [c?.synapse_write_user ?? ''],
        synapse_write_password: [c?.synapse_write_password ?? '']
      });
      return;
    }

    if (type === 'SNOWFLAKE') {
      const c = existing as SnowflakeCredentials | null;
      this.form = this.fb.group({
        account: [c?.account ?? '', Validators.required],
        user: [c?.user ?? '', Validators.required],
        warehouse: [c?.warehouse ?? '', Validators.required],
        private_key_path: [c?.private_key_path ?? '', Validators.required],
        write_user: [c?.write_user ?? ''],
        write_private_key_path: [c?.write_private_key_path ?? '']
      });
      return;
    }

    const c = existing as DatabricksCredentials | null;
    this.form = this.fb.group({
      server_hostname: [c?.server_hostname ?? '', Validators.required],
      http_path: [c?.http_path ?? '', Validators.required],
      access_token: [c?.access_token ?? '', Validators.required],
      write_access_token: [c?.write_access_token ?? ''],
      catalog: [c?.catalog ?? ''],
      schema: [c?.schema ?? '']
    });
  }

  private fail(err: HttpErrorResponse): void {
    this.saving = false;
    const body = err.error as ApiErrorResponse | undefined;
    this.toastrService.error(body?.detail ?? body?.error?.message ?? this.firstFieldError(body) ?? 'Something went wrong. Please try again.');
  }

  private firstFieldError(body?: ApiErrorResponse): string | null {
    if (!body) {
      return null;
    }
    for (const key of Object.keys(body)) {
      if (key === 'detail' || key === 'error') {
        continue;
      }
      const value = (body as Record<string, unknown>)[key];
      if (Array.isArray(value) && value.length) {
        return `${key}: ${value[0]}`;
      }
      if (typeof value === 'string') {
        return `${key}: ${value}`;
      }
    }
    return null;
  }
}
